import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import type {
  CreateSurveyRunResponseDto,
  SurveyForIntakeDto,
  RefreshTokenResponseDto,
  SubmitSurveyRequestDto,
  SubmitSurveyResponseDto,
  SubmitContactRequestDto
} from '../models/intake-dto';

const BASE = `${environment.apiUrl}/api/intake`;

@Injectable({ providedIn: 'root' })
export class IntakeService {
  constructor(private readonly http: HttpClient) {}

  /** POST /api/intake/run - body: { surveyVersionId?, surveyTypeId? } */
  createRun(surveyVersionId?: number, surveyTypeId?: number): Observable<CreateSurveyRunResponseDto> {
    const body: { surveyVersionId?: number; surveyTypeId?: number } = {};
    if (surveyVersionId != null) body.surveyVersionId = surveyVersionId;
    if (surveyTypeId != null) body.surveyTypeId = surveyTypeId;
    return this.http.post<CreateSurveyRunResponseDto>(`${BASE}/run`, body).pipe(
      map((r) => ({ ...r, token: String(r.token) })),
      catchError((err) => throwError(() => this.normalizeError(err)))
    );
  }

  /** GET /api/intake/run/{surveyRunId}?token=guid */
  getSurvey(surveyRunId: number, token: string): Observable<SurveyForIntakeDto> {
    const params = new HttpParams().set('token', token);
    return this.http
      .get<SurveyForIntakeDto>(`${BASE}/run/${surveyRunId}`, { params })
      .pipe(catchError((err) => throwError(() => this.normalizeError(err))));
  }

  /** POST /api/intake/run/{surveyRunId}/refresh-token - body: { token } */
  refreshToken(surveyRunId: number, token: string): Observable<RefreshTokenResponseDto> {
    return this.http
      .post<RefreshTokenResponseDto>(`${BASE}/run/${surveyRunId}/refresh-token`, { token })
      .pipe(
        map((r) => ({ ...r, token: String(r.token) })),
        catchError((err) => throwError(() => this.normalizeError(err)))
      );
  }

  /** POST /api/intake/run/submit. Normalizes response to camelCase for templates (API may return PascalCase). */
  submit(request: SubmitSurveyRequestDto): Observable<SubmitSurveyResponseDto> {
    return this.http
      .post<SubmitSurveyResponseDto & { TotalScore?: number; Analysis?: string; CallToActionDisplayText?: string; CallToActionPromptFragment?: string }>(`${BASE}/run/submit`, request)
      .pipe(
        map((r) => this.normalizeSubmitResponse(r)),
        catchError((err) => throwError(() => this.normalizeError(err)))
      );
  }

  private normalizeSubmitResponse(r: SubmitSurveyResponseDto & { TotalScore?: number; Analysis?: string; CallToActionDisplayText?: string; CallToActionPromptFragment?: string }): SubmitSurveyResponseDto {
    return {
      totalScore: r.totalScore ?? r.TotalScore ?? 0,
      analysis: r.analysis ?? r.Analysis ?? undefined,
      callToActionDisplayText: r.callToActionDisplayText ?? r.CallToActionDisplayText ?? undefined,
      callToActionPromptFragment: r.callToActionPromptFragment ?? r.CallToActionPromptFragment ?? undefined
    };
  }

  /** POST /api/intake/run/submit-contact - submit contact from the results page after survey submit. */
  submitContact(request: SubmitContactRequestDto): Observable<{ success: boolean }> {
    return this.http
      .post<{ success: boolean }>(`${BASE}/run/submit-contact`, request)
      .pipe(catchError((err) => throwError(() => this.normalizeError(err))));
  }

  private normalizeError(err: { error?: { error?: string }; message?: string }): Error {
    const msg =
      err?.error?.error ?? err?.message ?? 'An error occurred.';
    return new Error(msg);
  }
}
